import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Search as SearchIcon, X } from "lucide-react";
import type { CatalogProduct, Collection } from "@/lib/catalog/products";
import { getCatalogRepository } from "@/lib/catalog/repository";
import { CategoryRegistry } from "@/lib/catalog/registry";
import { recordSearch } from "@/lib/admin/tracking/recent-searches";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackProductImage } from "@/lib/catalog/fallback-images";
import { startingPrice } from "@/lib/catalog/size-pricing";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  category: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/search")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Search — CB16 Maison" },
      {
        name: "description",
        content:
          "Search the CB16 catalog by product name, olfactive family, notes, or attributes — across every category.",
      },
      { property: "og:title", content: "Search — CB16 Maison" },
      { property: "og:description", content: "Find your next signature scent." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/search" }],
  }),
  component: SearchPage,
});

function SearchPage() {
  const { q, category } = Route.useSearch();
  const navigate = useNavigate({ from: "/search" });
  const [value, setValue] = useState(q);
  const [results, setResults] = useState<CatalogProduct[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(false);

  const query = q.trim();

  useEffect(() => {
    let cancelled = false;
    if (!query) { setResults([]); setCollections([]); return; }
    setLoading(true);
    const repo = getCatalogRepository();
    Promise.all([
      repo.searchProducts(query, category ? { categoryId: category } : undefined),
      repo.getCollections(),
    ]).then(([prods, cols]) => {
      if (cancelled) return;
      setResults(prods);
      const needle = query.toLowerCase();
      setCollections(cols.filter((c) =>
        c.name.toLowerCase().includes(needle) ||
        c.tagline.toLowerCase().includes(needle) ||
        c.description.toLowerCase().includes(needle),
      ));
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [query, category]);

  useEffect(() => {
    if (query) recordSearch(query, results.length);
    // Only record once per unique query resolution to avoid spam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, results.length]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogProduct[]>();
    for (const p of results) {
      const cid = p.categoryId ?? "personal-perfumes";
      const arr = map.get(cid) ?? [];
      arr.push(p);
      map.set(cid, arr);
    }
    return Array.from(map.entries()).map(([cid, prods]) => ({
      cid,
      name: CategoryRegistry.get(cid)?.metadata.name ?? cid,
      slug: CategoryRegistry.get(cid)?.metadata.slug ?? cid,
      products: prods,
    }));
  }, [results]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    navigate({ search: { q: value.trim(), category } });
  }
  function setCategory(cid: string) {
    navigate({ search: { q, category: cid } });
  }

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:px-10 lg:py-20">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
            Search
          </span>
          <h1
            className="mt-4 text-3xl font-normal text-black sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Find your scent
          </h1>
          <form onSubmit={submit} className="mt-8 flex items-center gap-2">
            <div className="relative flex-1">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/40" />
              <input
                type="search"
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Search notes, family, collection…"
                aria-label="Search catalog"
                className="w-full border border-black/20 bg-white py-4 pl-12 pr-10 text-base text-black placeholder:text-black/40 focus:border-[#C6A15B] focus:outline-none"
              />
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    setValue("");
                    navigate({ search: { q: "", category } });
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-black/40 hover:text-black"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="bg-black px-6 py-4 text-[11px] font-medium uppercase tracking-[0.28em] text-white transition hover:bg-[#C6A15B]"
            >
              Search
            </button>
          </form>
          <div className="mt-6 flex flex-wrap gap-2">
            <button type="button" onClick={() => setCategory("")}
              className={"border px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] transition " + (category === "" ? "border-[#C6A15B] bg-[#C6A15B] text-white" : "border-black/20 text-black/60 hover:border-[#C6A15B] hover:text-[#C6A15B]")}>All categories</button>
            {CategoryRegistry.all().map((p) => (
              <button key={p.metadata.id} type="button" onClick={() => setCategory(p.metadata.id)}
                className={"border px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] transition " + (category === p.metadata.id ? "border-[#C6A15B] bg-[#C6A15B] text-white" : "border-black/20 text-black/60 hover:border-[#C6A15B] hover:text-[#C6A15B]")}>{p.metadata.name}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 lg:px-10 lg:py-20">
        {!query ? (
          <p className="text-sm text-black/60">
            Try searching for “oud”, “rose”, “diffuser”, “roll-on”, or a note you love.
          </p>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.24em] text-black/60">
              {loading ? "Searching…" : `${results.length + collections.length} results for “${query}”`}
            </p>

            {collections.length > 0 && (
              <div className="mt-10">
                <h2 className="text-xs uppercase tracking-[0.28em] text-black/50">Collections</h2>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {collections.map((c) => (
                    <li key={c.id}>
                      <Link
                        to="/collections/$slug"
                        params={{ slug: c.id }}
                        className="block border border-black/10 p-5 transition hover:border-[#C6A15B]"
                      >
                        <p
                          className="text-lg text-black"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {c.name}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-[0.24em] text-[#C6A15B]">
                          {c.tagline}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {grouped.length === 0 && !loading && (
              <p className="mt-12 text-sm text-black/60">
                No products match your search. Try a broader term or{" "}
                <Link to="/shop" className="underline hover:text-[#C6A15B]">browse the full shop</Link>.
              </p>
            )}
            {grouped.map((g) => (
              <div key={g.cid} className="mt-12">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-xs uppercase tracking-[0.28em] text-black/50">{g.name}</h2>
                  <Link to="/shop/$category" params={{ category: g.slug }} className="text-[11px] uppercase tracking-[0.24em] text-black/60 transition hover:text-[#C6A15B]">
                    View all in {g.name}
                  </Link>
                </div>
                <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-4">
                  {g.products.map((p) => (
                    <li key={p.id}>
                      <Link
                        to="/fragrances/$slug"
                        params={{ slug: p.slug }}
                        className="group block"
                      >
                        <div className="overflow-hidden bg-[#faf7f2]">
                          <SafeImage
                            src={p.img}
                            fallback={fallbackProductImage(p.slug, p.categoryId)}
                            alt={p.name}
                            loading="lazy"
                            className="aspect-[3/4] w-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        </div>
                        <p
                          className="mt-3 text-sm text-black"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {p.name}
                        </p>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-black/50">
                          {p.family} · Starting From ₹{startingPrice(p)}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
